// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import HomePage from './HomePage';
import CleaningDashboard from './CleaningDashboard';
import Page1 from './page1-with-fleet-management';
import Page2 from './page2-with-vessel-floorplans';
import Page3 from './vessel-checkin-page3';
import Page4 from './vessel-checkin-page4';
import Page5 from './vessel-final-page';
import CharterAgreementPage from './CharterAgreementPage';
import CrewInviteForm from './CrewInviteForm';
import GatePage from './GatePage';
import CharterGuidePage from './CharterGuidePage';
import LogistikaPage from './LogistikaPage';
import FleetManagement from './FleetManagement';
import CompanyNews from './CompanyNews';
import ChartererDashboard from './ChartererDashboard';
import OwnerDashboard from './OwnerDashboard';
import OwnerProfile from './OwnerProfile';
import AdminPanel from './AdminPanel';
import TechnicalSupportChat from './TechnicalSupportChat';
import TechnicalManagerDashboard from './TechnicalManagerDashboard';
import WinterizationCheckin from './WinterizationCheckin';
import WinterMaintenanceInventory from './WinterMaintenanceInventory';
import WinterTakeOver from './WinterTakeOver';
import WinterSafetyEquipment from './WinterSafetyEquipment';
import RoutineCheckList from "./RoutineCheck/RoutineCheckList";
import RoutineCheckForm from "./RoutineCheck/RoutineCheckForm";
import RoutineCheckHistory from "./RoutineCheck/RoutineCheckHistory";
import RoutineCheckView from "./RoutineCheck/RoutineCheckView";
import TaskCategoryCheckin from './TaskCategoryCheckin';
import SyncIndicator from './SyncIndicator';
import PendingCheckinsBar from './components/PendingCheckinsBar';
import AddBoat from './AddBoat';
import ConsolidatedReports from './ConsolidatedReports';
import EditFloorPlan from './EditFloorPlan';
import ItemsManagementPage from './ItemsManagementPage';
import HotspotEditorPage from './HotspotEditorPage';
import EmailCampaignPage from './EmailCampaignPage';
import DocumentsManager from './DocumentsManager';
import { initializeAuth } from './authService';
import authService from './authService';
import { getVessels, migrateTasksFromLocalStorage, migrateInvoicesFromLocalStorage } from './services/apiService';
import PayPage from './PayPage';

// 🔥 Global auto-refresh interval (3 minutes)
const AUTO_REFRESH_INTERVAL = 3 * 60 * 1000; // 180000 ms

// 🔥 Production API URL (same as apiService.ts)
const API_BASE = 'https://yachtmanagementsuite.com/api';

// 🔒 SECURITY: Protected Admin Route - Block Owner access
const ProtectedAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [checked, setChecked] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      // 🔐 SECURITY (23/10/2026): επανα-επικύρωση ΣΤΟΝ SERVER — απενεργοποιημένος/
      // διαγραμμένος χρήστης (ή καθόλου συνεδρία) πετιέται στο login. Πριν ελεγχόταν
      // ΜΟΝΟ ο ρόλος από το (παλιό) sessionStorage, οπότε ένας ακυρωμένος υπάλληλος
      // με stale session έμπαινε κανονικά στο /admin.
      const user = await authService.revalidateCurrentUser();
      if (cancelled) return;

      // Καμία έγκυρη/ενεργή συνεδρία → login
      if (!user) { navigate('/', { replace: true }); return; }

      const role = user.role?.toUpperCase();
      // Check if user is Owner
      if (role === 'OWNER') {
        alert('Access Denied: Owners cannot access the admin area.');
        navigate('/owner-dashboard', { state: { ownerCode: user.code }, replace: true });
        return;
      }
      // 🧽 CLEANING crew: never the admin area — redirect to their dashboard
      if (role === 'CLEANING') {
        navigate('/cleaning-dashboard', { replace: true });
        return;
      }
      // Only allow ADMIN, TECHNICAL, BOOKING, ACCOUNTING, FINANCIAL roles
      const allowedRoles = ['ADMIN', 'TECHNICAL', 'BOOKING', 'ACCOUNTING', 'FINANCIAL'];
      if (!allowedRoles.includes(role)) {
        alert('Access Denied: You do not have permission to access this area.');
        navigate('/', { replace: true });
        return;
      }

      setChecked(true);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  if (!checked) return null;
  return <>{children}</>;
};

// Κεντρικό Context για συγχρονισμό δεδομένων

// 🔥 SCROLL TO TOP on route change — fixes "pages open scrolled to bottom"
function ScrollToTop() {
  const location = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
}

export const DataContext = React.createContext<any>(null);

// Navigation wrapper component
const NavigationWrapper: React.FC<{ pageNum: number; children: React.ReactNode }> = ({ 
  pageNum, 
  children 
}) => {
  const navigate = useNavigate();

  const handleNavigate = (direction: 'next' | 'prev') => {
    const routes = ['/', '/page1', '/page2', '/page3', '/page4', '/page5'];
    const currentIndex = routes.indexOf(window.location.pathname);
    
    console.log(`🚀 handleNavigate called: direction=${direction}, currentPath=${window.location.pathname}, currentIndex=${currentIndex}`);
    
    if (direction === 'next' && currentIndex < routes.length - 1) {
      const nextRoute = routes[currentIndex + 1];
      console.log(`➡️ Navigating to: ${nextRoute}`);
      navigate(nextRoute);
    } else if (direction === 'prev' && currentIndex > 0) {
      const prevRoute = routes[currentIndex - 1];
      console.log(`⬅️ Navigating to: ${prevRoute}`);
      navigate(prevRoute);
    }
  };

  return (
    <div>
      {React.cloneElement(children as React.ReactElement, { 
        onNavigate: handleNavigate,
        pageNum 
      })}
    </div>
  );
};

export const APP_VERSION = '6.534';

function App() {

  // Version check - auto reload on new version
  React.useEffect(() => {
    const checkVersion = async () => {
      try {
        const resp = await fetch('/version.json?t=' + Date.now());
        if (resp.ok) {
          const data = await resp.json();
          if (data.version === APP_VERSION) {
            // On the correct version -> clear the guard so a future update can reload again.
            sessionStorage.removeItem('version_reloaded');
          } else if (data.version && data.version !== APP_VERSION) {
            console.log('New version available:', data.version, '(current:', APP_VERSION + ')');
            // Guard: reload at most once per session per version (prevents infinite reload loop)
            const already = sessionStorage.getItem('version_reloaded');
            if (already === data.version) return;
            sessionStorage.setItem('version_reloaded', data.version);
            // Unregister service worker
            if ('serviceWorker' in navigator) {
              const registrations = await navigator.serviceWorker.getRegistrations();
              for (const reg of registrations) { await reg.unregister(); }
            }
            // Clear caches
            if ('caches' in window) {
              const names = await caches.keys();
              for (const name of names) { await caches.delete(name); }
            }
            window.location.reload();
          }
        }
      } catch (e) { console.log('Version check failed:', e); }
    };
    checkVersion();
    const interval = setInterval(checkVersion, 60 * 1000); // every 60s
    const onPageShow = (e: any) => { if (e && e.persisted) { checkVersion(); } };
    window.addEventListener('pageshow', onPageShow);
    return () => { clearInterval(interval); window.removeEventListener('pageshow', onPageShow); };
  }, []);

  console.log('🚀 APP LOADED - App component rendering');

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(true);

  // 🔐 BOOT GATE (23/10/2026 — incident PWA/κινητό: παλιά συνεδρία + cache έμπαινε):
  // ΠΡΙΝ ζωγραφιστεί οτιδήποτε, αν υπάρχει αποθηκευμένη συνεδρία ΠΡΟΣΩΠΙΚΟΥ, την
  // επικυρώνουμε ΣΤΟΝ SERVER (fail-closed). Μέχρι να απαντήσει → loading screen, ΟΧΙ
  // μενού. Αν απορριφθεί/offline → logout → ο χρήστης βλέπει login. Χωρίς συνεδρία →
  // ready αμέσως (δημόσια flows πελάτη δουλεύουν κανονικά).
  const [bootReady, setBootReady] = useState(() => {
    try { return !authService.getCurrentUser(); } catch (e) { return true; }
  });
  useEffect(() => {
    if (bootReady) return;
    let cancelled = false;
    authService.revalidateCurrentUser()
      .catch(() => { try { authService.logout(); } catch (e) {} return null; })
      .then((valid) => {
        if (cancelled) return;
        // Αν η συνεδρία ΑΠΟΡΡΙΦΘΗΚΕ (disabled/offline → logout) και δεν είμαστε ήδη
        // στο login → ανακατεύθυνση στο «/» (login), ΟΧΙ guest home. Μόνο login.
        let stillLoggedIn = true;
        try { stillLoggedIn = !!authService.getCurrentUser(); } catch (e) { stillLoggedIn = false; }
        if (!valid && !stillLoggedIn && window.location.pathname !== '/') {
          window.location.replace('/');
          return;  // η σελίδα ξαναφορτώνει στο «/» — δεν ζωγραφίζουμε τίποτα ενδιάμεσα
        }
        setBootReady(true);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔥 NEW: Global bookings state (shared across all pages)
  const [globalBookings, setGlobalBookings] = useState<any[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Shared data across all pages
  const [sharedData, setSharedData] = useState<any>({
    mode: 'in', // Default mode: 'in' or 'out'
    bookingNumber: '',
    vesselName: '',
    damages: [],
    photos: [],
    signatures: {},
    equipment: {},
    inventory: {}
  });

  // Track previous mode for change detection
  const [previousMode, setPreviousMode] = useState<string>('in');

  // 🔥 EMERGENCY: Fetch bookings with 5s abort timeout + guaranteed isRefreshing reset
  const fetchGlobalBookings = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    setIsRefreshing(true);
    try {
      console.log('[FETCH] Calling API...', new Date().toISOString());
      console.log('🔄 [Global Auto-Refresh] Fetching bookings from API...');
      // 🔐 ΦΑΣΗ 2: στείλε credential header (staff ή customer) ώστε το scoped bookings.php
      // να μην εξαρτάται μόνο από το cookie. Ανώνυμος → κανένα header → 401 (σωστά).
      let __h: any = {};
      try { const u = JSON.parse(sessionStorage.getItem('auth_current_user') || 'null'); if (u && u.code) __h['X-Employee-Code'] = u.code; } catch (e) {}
      try { const ct = sessionStorage.getItem('yms_cust_token'); if (ct) __h = { 'X-Customer-Token': ct }; } catch (e) {}
      const response = await fetch(`${API_BASE}/bookings.php`, { signal: controller.signal, headers: __h });
      const data = await response.json();
      if (data.success && data.bookings) {
        const bookingsList = Array.isArray(data.bookings) ? data.bookings : Object.values(data.bookings);
        setGlobalBookings(bookingsList);
        console.log('[FETCH] Got bookings:', bookingsList.length, 'Booking 1300:', bookingsList.find(b => String(b.bookingNumber || b.bookingCode || b.code || '').includes('1300')));
        setLastRefresh(new Date());
        window.dispatchEvent(new CustomEvent('globalBookingsRefreshed', {
          detail: { bookings: bookingsList, timestamp: Date.now() }
        }));
        console.log(`✅ [Global Auto-Refresh] Loaded ${bookingsList.length} bookings`);
      }
    } catch (error) {
      console.warn('⏱️ [Global Auto-Refresh] Fetch failed/timed out (non-blocking):', error);
    } finally {
      clearTimeout(timeoutId);
      setIsRefreshing(false);
    }
  }, []);

  // 🔥 NEW: Set up auto-refresh interval (3 minutes)
  useEffect(() => {
    // Initial fetch
    fetchGlobalBookings();

    // Set up interval
    refreshIntervalRef.current = setInterval(() => {
      console.log('⏰ [Global Auto-Refresh] 3-minute interval triggered');
      fetchGlobalBookings();
    }, AUTO_REFRESH_INTERVAL);

    console.log('✅ [Global Auto-Refresh] Started - refreshing every 3 minutes');

    // Cleanup on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        console.log('🛑 [Global Auto-Refresh] Stopped');
      }
    };
  }, [fetchGlobalBookings]);

  // 🔥 NEW: Listen for manual refresh requests from any component
  useEffect(() => {
    const handleRefreshRequest = () => {
      console.log('📢 [Global Auto-Refresh] Manual refresh requested');
      fetchGlobalBookings();
    };

    window.addEventListener('requestGlobalRefresh', handleRefreshRequest);

    return () => {
      window.removeEventListener('requestGlobalRefresh', handleRefreshRequest);
    };
  }, [fetchGlobalBookings]);

  // 🆕 Initialize authentication system on mount
  useEffect(() => {
    initializeAuth();
    console.log('✅ Authentication system initialized');
  }, []);

  // 🔥 KILL-SWITCH: listen for SW FORCE_RELOAD + trigger SW update on mount
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMsg = (e: MessageEvent) => {
      if (e.data === 'FORCE_RELOAD') {
        console.log('🔄 SW forced reload — clearing page');
        window.location.reload();
      }
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    navigator.serviceWorker.getRegistrations().then(function(regs) {
      regs.forEach(function(r) { r.update().catch(function() { /* silent */ }); });
    });
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, []);

  // 🔥 AUTO-MIGRATION: Upload localStorage data to API on app load
  useEffect(() => {
    const runMigration = async () => {
      try {
        console.log('🔄 [Auto-Migration] Starting automatic data migration...');

        // Fetch all vessels from API
        const vessels = await getVessels();

        if (!vessels || vessels.length === 0) {
          console.log('ℹ️ [Auto-Migration] No vessels found, skipping migration');
          return;
        }

        console.log(`📦 [Auto-Migration] Found ${vessels.length} vessels, checking for localStorage data...`);

        // Migrate data for each vessel
        for (const vessel of vessels) {
          const vesselId = vessel.id;
          const vesselName = vessel.name || String(vesselId);

          // Migrate tasks
          await migrateTasksFromLocalStorage(vesselId, vesselName);

          // Migrate invoices
          await migrateInvoicesFromLocalStorage(vesselId, vesselName);
        }

        console.log('✅ [Auto-Migration] Completed for all vessels');
      } catch (error) {
        console.error('❌ [Auto-Migration] Error during migration:', error);
      }
    };

    // Run migration after a short delay to not block initial render
    const timer = setTimeout(runMigration, 2000);

    return () => clearTimeout(timer);
  }, []);

  // 🆕 Load current booking number on mount (UI state only)
  useEffect(() => {
    try {
      const currentBooking = localStorage.getItem('currentBooking');
      if (currentBooking) {
        console.log('🔄 App.tsx: Loading current booking:', currentBooking);
        setSharedData(prev => ({
          ...prev,
          bookingNumber: currentBooking
        }));
      }
    } catch (e) {
      console.error('Error loading booking:', e);
    }
  }, []);

  // 🔥 Mode change detection - dispatch event to notify pages
  useEffect(() => {
    if (previousMode !== sharedData.mode) {
      console.log(`🔄 Mode changed: ${previousMode} → ${sharedData.mode}`);
      setPreviousMode(sharedData.mode);

      // Dispatch event to notify all pages (pages will handle data from API)
      window.dispatchEvent(new CustomEvent('modeChanged', {
        detail: { mode: sharedData.mode }
      }));

      console.log('✅ Mode change handled!');
    }
  }, [sharedData.mode, previousMode]);

  // 🆕 Listen for custom mode change event (same tab)
  useEffect(() => {
    const handleModeChange = (e: CustomEvent) => {
      setSharedData(prev => ({ ...prev, mode: e.detail.mode }));
    };

    window.addEventListener('modeChanged', handleModeChange as EventListener);
    
    return () => {
      window.removeEventListener('modeChanged', handleModeChange as EventListener);
    };
  }, []);

  // 🔥 Listen for booking change event
  useEffect(() => {
    const handleBookingChange = (e: CustomEvent) => {
      const newBookingNumber = e.detail?.bookingNumber;
      console.log('📢 App.tsx received bookingChanged event:', newBookingNumber);

      if (newBookingNumber) {
        setSharedData(prev => ({
          ...prev,
          bookingNumber: newBookingNumber
        }));
        console.log('✅ App.tsx: Booking updated to:', newBookingNumber);
      }
    };

    window.addEventListener('bookingChanged', handleBookingChange as EventListener);

    return () => {
      window.removeEventListener('bookingChanged', handleBookingChange as EventListener);
    };
  }, []);

  // Update shared data
  const updateSharedData = (updates: any) => {
    console.log('📝 App.tsx: updateSharedData called with:', updates);

    const newData = { ...sharedData, ...updates };
    setSharedData(newData);

    // Track current booking number (UI state only)
    if (updates.bookingNumber && updates.bookingNumber !== sharedData.bookingNumber) {
      console.log('📢 App.tsx: Dispatching bookingChanged event for:', updates.bookingNumber);
      localStorage.setItem('currentBooking', updates.bookingNumber);
      window.dispatchEvent(new CustomEvent('bookingChanged', {
        detail: { bookingNumber: updates.bookingNumber }
      }));
    }

    // Dispatch mode change event (no localStorage for booking data)
    if (updates.mode) {
      window.dispatchEvent(new CustomEvent('modeChanged', {
        detail: { mode: updates.mode }
      }));
      console.log('✅ App.tsx: Mode changed to:', updates.mode);
    }
  };

  // Add damage
  const addDamage = (damage: any) => {
    setSharedData({
      ...sharedData,
      damages: [...(sharedData.damages || []), damage]
    });
  };

  // Add photo
  const addPhoto = (photo: any) => {
    setSharedData({
      ...sharedData,
      photos: [...(sharedData.photos || []), photo]
    });
  };

  // Save to database (placeholder)
  const saveToDatabase = async () => {
    console.log('Saving to database:', sharedData);
    alert('Αποθηκεύτηκε! (προσωρινά στην κονσόλα)');
  };

  // Finalize check-in
  const finalizeCheckIn = async () => {
    console.log('Finalizing check-in');
    alert('Check-in ολοκληρώθηκε!');
  };

  // Finalize check-out
  const finalizeCheckOut = async () => {
    console.log('Finalizing check-out');
    alert('Check-out ολοκληρώθηκε!');
  };

  // Context value
  const contextValue = {
    data: sharedData,
    mode: sharedData.mode,
    bookingNumber: sharedData.bookingNumber,
    updateData: updateSharedData,
    addDamage,
    addPhoto,
    saveToDatabase,
    finalizeCheckIn,
    finalizeCheckOut,
    // 🔥 NEW: Global bookings with auto-refresh
    globalBookings,
    isRefreshing,
    lastRefresh,
    refreshBookings: fetchGlobalBookings, // Manual refresh function
  };

  // 🔐 BOOT GATE: μέχρι να επικυρωθεί η συνεδρία στον server, ΔΕΝ δείχνουμε τίποτα
  // από την εφαρμογή (ούτε μενού/home). Loading screen μόνο.
  if (!bootReady) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'linear-gradient(180deg,#1e90ff 0%,#87ceeb 100%)', color: '#fff',
                    fontFamily: "'Segoe UI',Tahoma,sans-serif", flexDirection: 'column', gap: 16 }}>
        <div style={{ width: 44, height: 44, border: '4px solid rgba(255,255,255,.4)', borderTopColor: '#fff',
                      borderRadius: '50%', animation: 'ymsspin 0.9s linear infinite' }} />
        <div style={{ fontSize: 15, opacity: .95 }}>⚓ Yacht Management Suite…</div>
        <style>{'@keyframes ymsspin{to{transform:rotate(360deg)}}'}</style>
      </div>
    );
  }

  return (
    <DataContext.Provider value={contextValue}>
      <Router>
        <ScrollToTop />
        {/* Sync status indicator - shows when refreshing data */}
        <SyncIndicator isRefreshing={isRefreshing} lastRefresh={lastRefresh} />
        {/* 🔴 v6.478 — ΜΟΝΙΜΗ μπάρα εκκρεμών check-in. Κορυφή ΚΑΘΕ οθόνης, δεν κλείνει,
            δεν φεύγει με refresh. Φεύγει μόνο όταν ο SERVER δει ολοκληρωμένο submit. */}
        <PendingCheckinsBar />
        <div style={{ minHeight: '100vh', background: '#eae8dc' }}>
          <Routes>
            {/* Home Page - Landing page με Welcome Section */}
            <Route path="/home" element={<HomePage />} />
            <Route path="/" element={<GatePage />} />
            <Route path="/charter-guide" element={<CharterGuidePage />} />
            
            {/* 🆕 Company News - Public access */}
            <Route path="/company-news" element={<CompanyNews />} />
            
            {/* 🆕 Charterer Dashboard - After login with charter code/date */}
            <Route path="/charterer-dashboard" element={<ChartererDashboard />} />
            
            {/* 🆕 Owner Dashboard - After login with owner code (shows list of boats) */}
            <Route path="/owner-dashboard" element={<OwnerDashboard />} />

            <Route path="/cleaning-dashboard" element={<CleaningDashboard />} />

            {/* 🆕 Owner Profile - Owner profile settings */}
            <Route path="/owner-profile" element={<OwnerProfile />} />

            {/* 🆕 Fleet Management - For Admin/Employee and Owner (boat details) */}
            <Route path="/fleet-management" element={<FleetManagement />} />
            
            {/* 🆕 Administrator - Same as Fleet Management (legacy route) - PROTECTED */}
            <Route path="/admin" element={
              <ProtectedAdminRoute>
                <FleetManagement />
              </ProtectedAdminRoute>
            } />
            
            {/* 🆕 NEW: Admin Panel - Code Management (ADMIN2025 only) */}
            <Route path="/admin-panel" element={<AdminPanel />} />

            {/* 🆕 NEW: Technical Support Chat - Customer chat with technical manager */}
            <Route path="/technical-support" element={<TechnicalSupportChat />} />

            {/* 🆕 NEW: Technical Manager Dashboard - View all support chats */}
            <Route path="/technical-manager" element={<TechnicalManagerDashboard />} />

            {/* 🆕 NEW: Winterization Check-in */}
            <Route path="/winterization" element={<WinterizationCheckin />} />

            {/* 🆕 NEW: Winter Maintenance Inventory */}
            <Route path="/winter-inventory" element={<WinterMaintenanceInventory />} />

            {/* 🆕 NEW: Winter Take Over Tracking */}
            <Route path="/winter-takeover" element={<WinterTakeOver />} />

            {/* 🆕 NEW: Winter Safety Equipment with Expiry Tracking */}
            <Route path="/winter-safety" element={<WinterSafetyEquipment />} />
n            {/* 🆕 NEW: Routine Check System */}
            <Route path="/routine-check" element={
              <ProtectedAdminRoute allowedRoles={["ADMIN", "TECHNICAL"]}>
                <RoutineCheckList />
              </ProtectedAdminRoute>
            } />
            <Route path="/routine-check/new/:vesselId" element={
              <ProtectedAdminRoute allowedRoles={["ADMIN", "TECHNICAL"]}>
                <RoutineCheckForm />
              </ProtectedAdminRoute>
            } />

            <Route path="/routine-check/history/:vesselId" element={
              <ProtectedAdminRoute allowedRoles={["ADMIN", "TECHNICAL"]}>
                <RoutineCheckHistory />
              </ProtectedAdminRoute>
            } />

            <Route path="/routine-check/view/:checkId" element={
              <ProtectedAdminRoute allowedRoles={["ADMIN", "TECHNICAL"]}>
                <RoutineCheckView />
              </ProtectedAdminRoute>
            } />

            {/* 🆕 NEW: Task Category Check-in Pages */}
            <Route path="/tasks/:category" element={<TaskCategoryCheckin />} />

            {/* 🆕 NEW: Add Boat - Full page vessel creator with floor plan */}
            <Route path="/add-boat" element={<AddBoat />} />

            {/* 🆕 NEW: Items Management - Master catalog & per-vessel items */}
            <Route path="/items-management" element={<ItemsManagementPage />} />

            {/* 🆕 NEW: Hotspot Editor - DB-backed floorplan hotspots per vessel */}
            <Route path="/hotspot-editor" element={<HotspotEditorPage />} />
            <Route path="/email-campaign" element={<EmailCampaignPage />} />
            <Route path="/documents" element={<DocumentsManager />} />

            {/* 🆕 NEW: Edit Floor Plan - Edit existing vessel floor plans */}
            <Route path="/edit-floorplan" element={<EditFloorPlan />} />

            {/* 🆕 NEW: Consolidated Reports - Financial summaries */}
            <Route path="/reports" element={<ConsolidatedReports />} />

            <Route path="/logistika" element={<LogistikaPage />} />
            {/* Charter Agreement & Documents Page */}
            <Route path="/charter-agreement" element={<CharterAgreementPage />} />
            <Route path="/crew-invite/:token" element={<CrewInviteForm />} />
            {/* Δημόσιος σύνδεσμος πληρωμής — ίδιο μοτίβο με το crew-invite:
                ο πελάτης το ανοίγει χωρίς λογαριασμό. Η σελίδα και ο service
                είναι ΤΑ ΙΔΙΑ αρχεία με το v2· το api/pay-link.php είναι shim
                που τρέχει τον κώδικα του v2 πάνω στην ΙΔΙΑ βάση. */}
            <Route path="/pay/:token" element={<PayPage />} />
            
            {/* Page 1 - Fleet Management & Booking Details */}
            <Route path="/page1" element={
              <NavigationWrapper pageNum={1}>
                <Page1 />
              </NavigationWrapper>
            } />
            
            {/* Page 2 - Acceptance/Delivery Statement */}
            <Route path="/page2" element={
              <NavigationWrapper pageNum={2}>
                <Page2 />
              </NavigationWrapper>
            } />
            
            {/* Page 3 - Safety/Cabin/Optional Equipment */}
            <Route path="/page3" element={
              <NavigationWrapper pageNum={3}>
                <Page3 />
              </NavigationWrapper>
            } />
            
            {/* Page 4 - Interactive Vessel Floorplans */}
            <Route path="/page4" element={
              <NavigationWrapper pageNum={4}>
                <Page4 />
              </NavigationWrapper>
            } />
            
            {/* Page 5 - Final Check-in/out */}
            <Route path="/page5" element={
              <NavigationWrapper pageNum={5}>
                <Page5 />
              </NavigationWrapper>
            } />
          </Routes>
        </div>
      </Router>
    </DataContext.Provider>
  );
}

export default App;
